import React, { useState } from "react";
import { api } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Heart, Repeat2, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const QUICK = [10, 25, 50, 100, 300, 500, 1000];

export default function Doneaza() {
  const [amount, setAmount] = useState(50);
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    anonymous: false,
    recurring: false,
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (amount < 5 || amount > 9000) {
      toast.error("Suma trebuie între 5 și 9000 RON");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount,
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la donație");
      setLoading(false);
    }
  };

  const buttonLabel = loading
    ? "Se procesează..."
    : form.recurring
      ? `Donez recurent ${amount} RON`
      : `Donez ${amount} RON`;

  return (
    <div data-testid="page-doneaza" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <header className="text-center mb-10">
        <Heart className="w-12 h-12 text-[#D4A63D] mx-auto mb-4" />
        <p className="text-xs tracking-[0.3em] text-[#D4A63D] uppercase mb-4">Susține această misiune</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#F8F8F4]">Sprijină această muncă pentru copii</h1>
        <p className="mt-5 text-[#A0AABF] text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          Contribuie și tu la mentenanța și dezvoltarea portalului <span className="text-white">Tati și Sofia</span>, printr-o donație simplă sau recurentă!
        </p>
        <p className="mt-4 text-[#F8F8F4]/90 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
          Nu donezi doar pentru un site. Contribui pentru ca <span className="text-white">părinții aflați în suferință</span> să găsească informații, sprijin și speranță, iar <span className="text-white">vocile copiilor prinși în conflictele adulților</span> să fie auzite.
        </p>
        <p className="mt-4 text-[#A0AABF] text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
          Fiecare contribuție ne ajută să continuăm munca de conștientizare, educație și sprijin pentru părinți și copii.
        </p>
      </header>

      <form onSubmit={submit} className="cinematic-card rounded-lg p-6 md:p-10 space-y-6">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#A0AABF] mb-3 block">Suma (RON)</label>
          <div className="text-center mb-6">
            <span className="font-display text-6xl md:text-7xl gold-text">{amount}</span>
            <span className="text-[#A0AABF] ml-2">RON</span>
          </div>
          <Slider
            data-testid="donate-slider"
            value={[amount]}
            onValueChange={(v) => setAmount(v[0])}
            min={5}
            max={9000}
            step={5}
          />
          <div className="mt-5 flex gap-2 flex-wrap">
            {QUICK.map((v) => (
              <button
                type="button"
                key={v}
                data-testid={`donate-${v}`}
                onClick={() => setAmount(v)}
                className={`px-4 py-2 rounded-sm text-sm border ${
                  amount === v
                    ? "border-[#D4A63D] bg-[#D4A63D]/10 text-[#D4A63D]"
                    : "border-white/10 text-[#A0AABF] hover:border-[#D4A63D]/30"
                }`}
              >
                {v} RON
              </button>
            ))}
          </div>

          {/* Anonymous + Recurring — SAME ROW, INDEPENDENT */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-sm border cursor-pointer transition text-sm ${
                form.anonymous
                  ? "border-[#D4A63D]/60 bg-[#D4A63D]/10 text-white"
                  : "border-white/10 text-[#A0AABF] hover:border-white/25"
              }`}
            >
              <input
                type="checkbox"
                data-testid="don-anon"
                checked={form.anonymous}
                onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                className="accent-[#D4A63D] w-4 h-4"
              />
              <Heart className="w-4 h-4 shrink-0" />
              <span>Donație anonimă</span>
            </label>

            <label
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-sm border cursor-pointer transition text-sm ${
                form.recurring
                  ? "border-[#E89BB8]/60 bg-[#E89BB8]/10 text-white"
                  : "border-white/10 text-[#A0AABF] hover:border-white/25"
              }`}
            >
              <input
                type="checkbox"
                data-testid="don-recurring"
                checked={form.recurring}
                onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
                className="accent-[#E89BB8] w-4 h-4"
              />
              <Repeat2 className="w-4 h-4 shrink-0" />
              <span>Donație recurentă</span>
            </label>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <input
            data-testid="don-name"
            placeholder="Nume (opțional)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm"
          />
          <input
            data-testid="don-email"
            type="email"
            placeholder={form.recurring ? "Email (necesar pentru donație recurentă)" : "Email (opțional)"}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm"
          />
        </div>
        <textarea
          data-testid="don-msg"
          rows={3}
          placeholder="Mesaj de susținere (opțional)"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-3 text-sm"
        />

        <button
          data-testid="don-submit"
          disabled={loading}
          className="btn-gold rounded-sm py-4 w-full text-sm uppercase tracking-widest font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {buttonLabel}
        </button>

        <div className="text-xs text-[#6C7A9C] text-center space-y-1">
          <p className="inline-flex items-center gap-1.5 justify-center">
            <ShieldCheck className="w-3.5 h-3.5" />
            Plată securizată prin Stripe. Sumele acceptate sunt între 5 și 9.000 RON.
          </p>
          {form.recurring && (
            <p data-testid="don-recurring-note" className="text-[#E89BB8]/80">
              Donația recurentă poate fi oprită oricând.
            </p>
          )}
        </div>
      </form>
    </div>
  );
}
